import { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LoaderCircle, ShieldCheck, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const { resetPassword, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const invalidLink = useMemo(() => !email || !token, [email, token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      const res = await resetPassword(email, token, password);
      toast.success(res?.message || "Contraseña actualizada");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <img src="/brand/emauran.png" alt="EMAURAN by OOMINING" className="h-16 w-auto object-contain dark:[filter:brightness(0)_invert(1)]" />
        </div>
        <div className="flex items-center gap-2 text-primary mb-2">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-xs uppercase tracking-widest">Nueva contraseña</span>
        </div>
        <h2 className="font-head font-bold text-2xl tracking-tight">Restablecer contraseña</h2>

        {invalidLink ? (
          <div className="mt-4 text-sm text-muted-foreground" data-testid="reset-invalid">
            El enlace es inválido o está incompleto. Solicita uno nuevo desde “¿Olvidaste tu contraseña?”.
            <button
              onClick={() => navigate("/", { replace: true })}
              data-testid="reset-goto-login"
              className="mt-4 flex items-center gap-1 text-primary hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> Ir a iniciar sesión
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-4" data-testid="reset-form">
            <p className="text-muted-foreground text-sm">Para <span className="font-medium text-foreground">{email}</span></p>
            <div>
              <Label className="text-xs">Nueva contraseña</Label>
              <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Confirmar contraseña</Label>
              <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1" />
            </div>
            <p className="text-[11px] text-muted-foreground">Mínimo 8 caracteres con mayúscula, minúscula, número y símbolo.</p>
            <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full rounded-sm mt-2">
              {loading && <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />}
              Guardar contraseña
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
